import {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {Box, Button, Paper, Stack, Typography} from '@mui/material';
import useAuth from '../hooks/useAuth';
import axios from '../utils/axios';
import {MARKETING_PREFERENCES_EVENT, getMarketingConsent, getMarketingConsentTimestamp, initializeMarketingTracking, marketingTrackingEnabled,
  setMarketingConsent, trackMarketingPage, trackPurchase} from '../lib/marketingTracking';

const RETURN_KEY = '1lead.checkout-return';
const rememberReturn = value => {try {sessionStorage.setItem(RETURN_KEY, value);} catch { /* Memory state still works. */ }};
const readReturn = () => {try {return sessionStorage.getItem(RETURN_KEY) || '';} catch {return '';}};
const clearReturn = () => {try {sessionStorage.removeItem(RETURN_KEY);} catch { /* Best effort. */ }};
let consentSync = Promise.resolve();

export default function MarketingTracking() {
  const {pathname, search} = useLocation();
  const {isAuthenticated, user} = useAuth();
  const enabled = marketingTrackingEnabled();
  const [consent, setConsent] = useState(getMarketingConsent);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [sessionId, setSessionId] = useState(readReturn);

  useEffect(() => {
    const update = () => setConsent(getMarketingConsent());
    const openPreferences = () => setPreferencesOpen(true);
    window.addEventListener('marketing-consent-changed', update);
    window.addEventListener(MARKETING_PREFERENCES_EVENT, openPreferences);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener('marketing-consent-changed', update);
      window.removeEventListener(MARKETING_PREFERENCES_EVENT, openPreferences);
      window.removeEventListener('storage', update);
    };
  }, []);

  useEffect(() => {
    const id = new URLSearchParams(search).get('session_id');
    if (/^cs_[A-Za-z0-9_]{1,200}$/.test(id || '')) {rememberReturn(id); setSessionId(id);}
  }, [search]);

  useEffect(() => {
    if (!enabled) return;
    initializeMarketingTracking();
    if (consent === true) trackMarketingPage();
  }, [enabled, consent, pathname]);

  useEffect(() => {
    if (!enabled || !isAuthenticated || !user?.id || consent === null) return undefined;
    let cancelled = false;
    let timer;
    let attempts = 0;
    const sync = () => {
      attempts += 1;
      consentSync = consentSync.catch(() => {}).then(() => {
        if (!cancelled) return axios.post('/api/checkout/tracking-consent', {granted: consent, decidedAt: getMarketingConsentTimestamp()});
        return undefined;
      }).catch(() => {
        if (!cancelled && attempts < 4) timer = window.setTimeout(sync, 2000);
      });
    };
    sync();
    return () => {cancelled = true; window.clearTimeout(timer);};
  }, [enabled, isAuthenticated, user?.id, consent]);

  useEffect(() => {
    if (!enabled || !isAuthenticated || !user?.id || !sessionId || consent !== true) return undefined;
    let cancelled = false;
    let timer;
    let attempts = 0;
    const verify = async () => {
      attempts += 1;
      try {
        const response = await axios.get('/api/checkout/receipt', {params: {sessionId}, cache: false});
        if (cancelled) return;
        const receipt = response?.data?.data ?? response?.data;
        if (receipt?.status === 'ignored') {clearReturn(); return;}
        if (receipt?.status === 'paid' && trackPurchase(receipt)) {clearReturn(); return;}
      } catch (error) {
        if ([400, 403, 404].includes(error?.status || error?.response?.status)) {clearReturn(); return;}
      }
      if (!cancelled && attempts < 8) timer = window.setTimeout(verify, 2000);
    };
    verify();
    return () => {cancelled = true; window.clearTimeout(timer);};
  }, [enabled, isAuthenticated, user?.id, sessionId, consent]);

  const choose = granted => {setMarketingConsent(granted); setConsent(granted); setPreferencesOpen(false);};
  if (!enabled) return null;
  if (consent === true && !preferencesOpen) return null;
  if (consent !== null && !preferencesOpen) return (
    <Button size="small" onClick={() => setPreferencesOpen(true)} aria-label="Preferências de privacidade"
      sx={{position: 'fixed', bottom: 8, left: 8, zIndex: theme => theme.zIndex.snackbar, bgcolor: 'background.paper'}}>
      Privacidade
    </Button>
  );
  return (
    <Paper role="region" aria-label="Preferências de privacidade" elevation={8}
      sx={{position: 'fixed', bottom: 16, left: 16, right: 16, maxWidth: 580, p: 2.5, zIndex: theme => theme.zIndex.snackbar}}>
      <Typography variant="subtitle2" gutterBottom>Sua privacidade</Typography>
      <Typography variant="body2" color="text.secondary">
        Com sua autorização, usamos cookies do Google e da Meta para medir visitas, cadastros e compras e melhorar nossos anúncios.
        Você pode alterar sua escolha na Política de Privacidade.
      </Typography>
      <Box sx={{mt: 2}}>
        <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
          <Button variant="outlined" onClick={() => choose(false)}>Apenas essenciais</Button>
          <Button variant="contained" onClick={() => choose(true)}>Aceitar</Button>
        </Stack>
      </Box>
    </Paper>
  );
}
